// FinancialManagementTab.tsx - Cost ledger + live AWS/LLM/revenue, inline edit, collapsible, CSV per section
import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, Grid, CircularProgress, Alert, Button,
  Table, TableBody, TableCell, TableHead, TableRow, TextField, MenuItem,
  IconButton, Chip, Stack, Collapse,
} from '@mui/material';
import { Delete, Edit, Add, Refresh, Close, Check, Download, ExpandMore, ExpandLess } from '@mui/icons-material';
import { api } from '../../utils/api';

interface CostItem {
  id: number; name: string; category: string; amount_usd: number;
  cadence: string; vendor: string | null; started_at: string | null; active: boolean;
}
interface Summary {
  monthlyTotal: number; annualTotal: number; oneTimeTotal: number;
  byCategory: Record<string, number>;
}

const CATEGORIES = ['development', 'operational', 'llm', 'other'];
const CADENCES = [
  { value: 'monthly', label: 'Monthly' },
  { value: 'annual', label: 'Annual' },
  { value: 'one_time', label: 'One-time' },
];
const teal = '#2D6E6A';
const PRO_MONTHLY_PRICE = 0; // set your real pro price
const fmt = (n: number) => '$' + (n ?? 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]) {
  const esc = (v: any) => { const s = String(v ?? ''); return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s; };
  const csv = [headers.map(esc).join(','), ...rows.map(r => r.map(esc).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

const StatTile: React.FC<{ label: string; value: string; sub?: string }> = ({ label, value, sub }) => (
  <Card variant="outlined" sx={{ borderRadius: 2 }}>
    <CardContent>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" fontWeight={700}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </CardContent>
  </Card>
);

const SectionHeader: React.FC<{ title: string; open: boolean; onToggle: () => void; onExport?: () => void }> = ({ title, open, onToggle, onExport }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }} onClick={onToggle}>
      <IconButton size="small">{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
      <Typography variant="subtitle1" fontWeight={600}>{title}</Typography>
    </Box>
    {onExport && <Button size="small" startIcon={<Download />} onClick={onExport} sx={{ color: teal }}>CSV</Button>}
  </Box>
);

const emptyForm = { name: '', category: 'development', amount_usd: '', cadence: 'monthly', vendor: '' };

const FinancialManagementTab: React.FC = () => {
  const [items, setItems] = useState<CostItem[]>([]);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [llmCost, setLlmCost] = useState<number | null>(null);
  const [proCount, setProCount] = useState<number | null>(null);
  const [awsCost, setAwsCost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addForm, setAddForm] = useState<any>(emptyForm);
  const [saving, setSaving] = useState(false);
  // inline edit state
  const [editId, setEditId] = useState<number | null>(null);
  const [editRow, setEditRow] = useState<any>(emptyForm);
  // collapsible: summary open, details collapsed
  const [openSummary, setOpenSummary] = useState(true);
  const [openAws, setOpenAws] = useState(false);
  const [openItems, setOpenItems] = useState(false);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const response = await api.get('/admin/cost-items');
      const res = await response.json();
      if (response.ok && res.success) { setItems(res.items || []); setSummary(res.summary || null); setAwsCost(res.aws || null); }
      else setError(res.error || 'Failed to load cost items');
      try {
        const aiRes = await api.get('/admin/ai-operations?days=30');
        const aiJson = await aiRes.json();
        if (aiRes.ok && aiJson.success) { setLlmCost(aiJson.window?.summary?.totalCostUsd ?? 0); }
      } catch (e) { /* non-fatal */ }
      try {
        const anRes = await api.get('/admin/analytics/overview');
        const anJson = await anRes.json();
        if (anRes.ok && anJson.success) {
          const tiers = anJson.users?.by_tier || [];
          const pro = tiers.find((t: any) => (t.tier || '').toLowerCase() === 'pro');
          setProCount(pro ? pro.count : 0);
        }
      } catch (e) { /* non-fatal */ }
    } catch (e) { setError('Failed to load cost items'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const addSubmit = async () => {
    if (!addForm.name.trim()) { setError('Name is required'); return; }
    setSaving(true); setError(null);
    try {
      const payload = { ...addForm, amount_usd: Number(addForm.amount_usd) || 0, active: true };
      const response = await api.post('/admin/cost-items', payload);
      const res = await response.json();
      if (response.ok && res.success) { setAddForm(emptyForm); await load(); }
      else setError(res.error || 'Save failed');
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const startEdit = (it: CostItem) => {
    setEditId(it.id);
    setEditRow({ name: it.name, category: it.category, amount_usd: String(it.amount_usd), cadence: it.cadence, vendor: it.vendor || '' });
  };
  const cancelEdit = () => { setEditId(null); setEditRow(emptyForm); };
  const saveEdit = async (id: number) => {
    setSaving(true); setError(null);
    try {
      const payload = { ...editRow, amount_usd: Number(editRow.amount_usd) || 0, active: true, id };
      const response = await api.put('/admin/cost-items', payload);
      const res = await response.json();
      if (response.ok && res.success) { cancelEdit(); await load(); }
      else setError(res.error || 'Save failed');
    } catch (e) { setError('Save failed'); }
    finally { setSaving(false); }
  };

  const remove = async (id: number) => {
    if (!window.confirm('Delete this cost item?')) return;
    try {
      const response = await api.delete('/admin/cost-items?id=' + id);
      const res = await response.json();
      if (response.ok && res.success) await load(); else setError(res.error || 'Delete failed');
    } catch (e) { setError('Delete failed'); }
  };

  const exportSummary = () => downloadCSV('financial_summary.csv', ['Metric', 'Value'], [
    ['Monthly burn', summary?.monthlyTotal ?? 0],
    ['Annual burn', summary?.annualTotal ?? 0],
    ['One-time costs', summary?.oneTimeTotal ?? 0],
    ['LLM / AI cost (30d)', llmCost ?? 0],
    ['Notional MRR', (proCount ?? 0) * PRO_MONTHLY_PRICE],
    ['AWS cost (MTD)', awsCost?.total ?? 0],
  ]);
  const exportAws = () => downloadCSV('aws_cost_by_service.csv', ['Service', 'Cost (USD)'],
    (awsCost?.byService || []).map((s: any) => [s.service, s.amount]));
  const exportItems = () => downloadCSV('cost_items.csv', ['Name', 'Category', 'Amount', 'Cadence', 'Vendor'],
    items.map(it => [it.name, it.category, it.amount_usd, it.cadence, it.vendor || '']));

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Financial Management</Typography>
          <Typography variant="body2" color="text.secondary">
            Cost of operations. Recurring costs are normalized to a monthly figure; one-time costs are listed separately.
          </Typography>
        </Box>
        <Button size="small" startIcon={<Refresh />} onClick={load} sx={{ color: teal }}>Refresh</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : (
        <>
          {/* SUMMARY */}
          <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <SectionHeader title="Summary" open={openSummary} onToggle={() => setOpenSummary(!openSummary)} onExport={exportSummary} />
              <Collapse in={openSummary}>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                  <Grid item xs={12} sm={4}><StatTile label="Monthly burn" value={fmt(summary?.monthlyTotal || 0)} sub="recurring, normalized" /></Grid>
                  <Grid item xs={12} sm={4}><StatTile label="Annual burn" value={fmt(summary?.annualTotal || 0)} sub="monthly x 12" /></Grid>
                  <Grid item xs={12} sm={4}><StatTile label="One-time costs" value={fmt(summary?.oneTimeTotal || 0)} sub="not in monthly burn" /></Grid>
                  <Grid item xs={12} sm={4}><StatTile label="LLM / AI cost (30d)" value={llmCost === null ? "\u2014" : fmt(llmCost)} sub="usage-based, from AI Ops" /></Grid>
                  <Grid item xs={12} sm={4}><StatTile label="Notional MRR" value={proCount === null ? "\u2014" : fmt(proCount * PRO_MONTHLY_PRICE)} sub={proCount === null ? "from tier counts" : (proCount + " pro x " + fmt(PRO_MONTHLY_PRICE))} /></Grid>
                  <Grid item xs={12} sm={4}><StatTile label="AWS cost (month-to-date)" value={awsCost ? fmt(awsCost.total) : "\u2014"} sub="live, from Cost Explorer" /></Grid>
                </Grid>
                {summary && Object.keys(summary.byCategory || {}).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Monthly burn by category</Typography>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      {Object.entries(summary.byCategory).map(([cat, amt]) => (
                        <Chip key={cat} label={cat + ': ' + fmt(amt as number)} sx={{ bgcolor: '#E0ECEA' }} />
                      ))}
                    </Stack>
                  </Box>
                )}
              </Collapse>
            </CardContent>
          </Card>

          {/* AWS BY SERVICE */}
          {awsCost && awsCost.byService && awsCost.byService.length > 0 && (
            <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
              <CardContent>
                <SectionHeader title="AWS cost by service (month-to-date)" open={openAws} onToggle={() => setOpenAws(!openAws)} onExport={exportAws} />
                <Collapse in={openAws}>
                  <Typography variant="caption" color="text.secondary">Live from Cost Explorer. Total: {fmt(awsCost.total)}</Typography>
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead><TableRow><TableCell>Service</TableCell><TableCell align="right">Cost</TableCell></TableRow></TableHead>
                    <TableBody>
                      {awsCost.byService.map((sv: any) => (
                        <TableRow key={sv.service}><TableCell>{sv.service}</TableCell><TableCell align="right">{fmt(sv.amount)}</TableCell></TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Collapse>
              </CardContent>
            </Card>
          )}

          {/* COST ITEMS (inline edit) */}
          <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
            <CardContent>
              <SectionHeader title="Cost items" open={openItems} onToggle={() => setOpenItems(!openItems)} onExport={exportItems} />
              <Collapse in={openItems}>
                {items.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>No cost items yet. Add one below.</Typography>
                ) : (
                  <Table size="small" sx={{ mt: 1 }}>
                    <TableHead><TableRow>
                      <TableCell>Name</TableCell><TableCell>Category</TableCell>
                      <TableCell align="right">Amount</TableCell><TableCell>Cadence</TableCell>
                      <TableCell>Vendor</TableCell><TableCell align="right">Actions</TableCell>
                    </TableRow></TableHead>
                    <TableBody>
                      {items.map(it => editId === it.id ? (
                        <TableRow key={it.id}>
                          <TableCell><TextField size="small" value={editRow.name} onChange={e => setEditRow({ ...editRow, name: e.target.value })} /></TableCell>
                          <TableCell><TextField size="small" select value={editRow.category} onChange={e => setEditRow({ ...editRow, category: e.target.value })}>{CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></TableCell>
                          <TableCell align="right"><TextField size="small" type="number" value={editRow.amount_usd} onChange={e => setEditRow({ ...editRow, amount_usd: e.target.value })} sx={{ width: 100 }} /></TableCell>
                          <TableCell><TextField size="small" select value={editRow.cadence} onChange={e => setEditRow({ ...editRow, cadence: e.target.value })}>{CADENCES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</TextField></TableCell>
                          <TableCell><TextField size="small" value={editRow.vendor} onChange={e => setEditRow({ ...editRow, vendor: e.target.value })} /></TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => saveEdit(it.id)} disabled={saving}><Check fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={cancelEdit}><Close fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ) : (
                        <TableRow key={it.id} sx={{ opacity: it.active ? 1 : 0.5 }}>
                          <TableCell>{it.name}</TableCell>
                          <TableCell><Chip size="small" label={it.category} /></TableCell>
                          <TableCell align="right">{fmt(it.amount_usd)}</TableCell>
                          <TableCell>{it.cadence}</TableCell>
                          <TableCell>{it.vendor || '\u2014'}</TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => startEdit(it)}><Edit fontSize="small" /></IconButton>
                            <IconButton size="small" onClick={() => remove(it.id)}><Delete fontSize="small" /></IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Collapse>
            </CardContent>
          </Card>

          {/* ADD COST ITEM (bottom) */}
          <Card variant="outlined" sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Add cost item</Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}><TextField fullWidth size="small" label="Name" value={addForm.name} onChange={e => setAddForm({ ...addForm, name: e.target.value })} /></Grid>
                <Grid item xs={6} sm={2}><TextField fullWidth size="small" select label="Category" value={addForm.category} onChange={e => setAddForm({ ...addForm, category: e.target.value })}>{CATEGORIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}</TextField></Grid>
                <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Amount (USD)" type="number" value={addForm.amount_usd} onChange={e => setAddForm({ ...addForm, amount_usd: e.target.value })} /></Grid>
                <Grid item xs={6} sm={2}><TextField fullWidth size="small" select label="Cadence" value={addForm.cadence} onChange={e => setAddForm({ ...addForm, cadence: e.target.value })}>{CADENCES.map(c => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}</TextField></Grid>
                <Grid item xs={6} sm={2}><TextField fullWidth size="small" label="Vendor" value={addForm.vendor} onChange={e => setAddForm({ ...addForm, vendor: e.target.value })} /></Grid>
                <Grid item xs={12} sm={1} sx={{ display: 'flex', alignItems: 'center' }}>
                  <Button variant="contained" onClick={addSubmit} disabled={saving} sx={{ bgcolor: teal, minWidth: 0 }}><Add /></Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default FinancialManagementTab;
